import { cn, Text } from '@bridebook/aura';
import {
  AI_PLANNER_Z_INDEX,
  aiPlannerRingBackground,
} from '@bridebook/web/lib/ai-planner/constants';
import { useTypedMessage } from '@bridebook/web/lib/ai-planner/hooks/use-typed-message';
import { AnimatePresence, m, type Variants } from 'motion/react';
import styles from './planner-bubble.module.css';

// How long the dots run before the words start
const DOTS_MS = 400;

// Pops out of the button with an overshoot, and shrinks back into it
const bubbleVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.6,
    transition: {
      opacity: { duration: 0.2, ease: 'easeIn' },
      scale: { duration: 0.22, ease: 'easeIn' },
    },
  },
  shown: {
    opacity: 1,
    scale: 1,
    transition: {
      opacity: { duration: 0.16, ease: 'easeOut' },
      scale: { duration: 0.38, ease: [0.26, 1.44, 0.42, 1] },
    },
  },
};

const TypingDots = () => (
  <span className="flex h-5 min-w-10 items-center justify-center gap-1" aria-hidden>
    {[0, 1, 2].map((dot) => (
      <span key={dot} className={cn('size-1.5 rounded-full bg-text-primary', styles.dot)} />
    ))}
  </span>
);

/**
 * The bubble: a gradient ring around a soft fill, with the message typed inside.
 * A shimmer runs along the bottom until the message is complete, then the bubble waves.
 * It mounts on each show, so every show starts from the dots.
 */
const BubbleBody = ({ message }: { message: string }) => {
  const { isWaiting, typed, isComplete } = useTypedMessage(message, DOTS_MS);

  return (
    <span
      className={cn(
        // The ring shows through 3px of transparent border, inside 12px of padding.
        // The shimmer is clipped to the fill.
        'relative isolate flex origin-bottom-right items-center overflow-hidden rounded-xl border-3 border-transparent p-[9px] shadow-[0_5px_15px_rgba(0,0,0,0.05)]',
        isComplete && styles.hello,
      )}
      style={aiPlannerRingBackground('var(--color-surface-tertiary)')}>
      {!isComplete && <span aria-hidden className={styles.shimmer} />}
      {/* Screen readers get the message whole straight away */}
      <span className="sr-only">{message}</span>
      {isWaiting ? (
        <TypingDots />
      ) : (
        // Positioned, so it paints over the shimmer
        <Text
          as="span"
          variant="typo-sm-semibold"
          aria-hidden
          className={cn('relative block break-words text-text-primary', styles.copy)}>
          {typed}
        </Text>
      )}
    </span>
  );
};

type PlannerBubbleProps = {
  // What the planner says
  message: string;
  isShown: boolean;
  // Makes the bubble clickable, e.g. to open the planner
  onClick?: () => void;
  className?: string;
};

/**
 * A short message from the planner above its entry button: the greeting on a first visit,
 * or a nudge about the page. Aligned with the button's right edge. Without a click handler,
 * taps pass through to the page. Needs a LazyMotion parent.
 */
export const PlannerBubble = ({ message, isShown, onClick, className }: PlannerBubbleProps) => (
  <AnimatePresence>
    {isShown && (
      <m.span
        role="status"
        className={cn(
          // Grows out of the corner nearest the button, 8px above it
          'absolute right-0 bottom-full mb-2 block w-max origin-bottom-right text-left',
          'max-w-[min(320px,calc(100vw-32px))]',
          onClick ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none',
          className,
        )}
        // Stops the click reaching the parent button, so it runs once
        onClick={
          onClick &&
          ((event) => {
            event.stopPropagation();
            onClick();
          })
        }
        style={{ zIndex: AI_PLANNER_Z_INDEX.bubble }}
        variants={bubbleVariants}
        initial="hidden"
        animate="shown"
        exit="hidden">
        <BubbleBody message={message} />
      </m.span>
    )}
  </AnimatePresence>
);
